import { useMemo, useState } from "react";
import { Drawer, Empty, Switch, Tag, Tooltip, Typography } from "antd";
import {
  TbAlertTriangle,
  TbCheck,
  TbClock,
  TbMail,
  TbMinus,
  TbPencil,
  TbPlayerPlay,
  TbRefreshDot,
  TbUser,
} from "react-icons/tb";
import { TaskEvent, TaskEventType } from "dtos/tasks/events.dto";
import { neutralTag, taskUi } from "components/TaskBoard/design";
import { describeDue } from "pages/TaskTemplate/deadline";
import { TaskStatus } from "dtos/tasks/template.dto";

type Formatted = {
  icon: React.ReactNode;
  line: React.ReactNode;
  tone: "neutral" | "good" | "warning" | "muted";
};

const TONE_COLOR = {
  neutral: taskUi.textMuted,
  good: "#15803d",
  warning: "#b45309",
  muted: taskUi.textFaint,
} as const;

const STATUS_WORD: Record<TaskStatus, string> = {
  [TaskStatus.PENDING]: "not started",
  [TaskStatus.ACTIVE]: "active",
  [TaskStatus.COMPLETE]: "complete",
  [TaskStatus.SKIPPED]: "skipped",
  [TaskStatus.DISQUALIFIED]: "disqualified",
};

const relative = (at: string | Date) => {
  const minutes = Math.round((Date.now() - +new Date(at)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / 1440)}d ago`;
};

const dayKey = (at: string | Date) => new Date(at).toDateString();

/**
 * Events store structure, not sentences, so the wording stays changeable and a renamed
 * person reads correctly in history written before the rename.
 */
const format = (event: TaskEvent, nameOf: (id?: string) => string): Formatted => {
  const who = event.by ? nameOf(String(event.by)) : "System";

  switch (event.type) {
    case TaskEventType.STATUS: {
      const { from, to, reason, cascadedFrom, automatic } = event.data;

      if (cascadedFrom) {
        return {
          icon: to === TaskStatus.COMPLETE ? <TbCheck size={13} /> : <TbMinus size={13} />,
          tone: "muted",
          line: <>Marked {STATUS_WORD[to]} along with its parent</>,
        };
      }

      if (automatic) {
        return {
          icon: <TbPlayerPlay size={12} />,
          tone: "neutral",
          line: <>Became {STATUS_WORD[to]}</>,
        };
      }

      return {
        icon: to === TaskStatus.COMPLETE ? <TbCheck size={13} /> : <TbMinus size={13} />,
        tone: to === TaskStatus.COMPLETE ? "good" : "neutral",
        line: (
          <>
            <strong>{who}</strong> moved it from {STATUS_WORD[from]} to {STATUS_WORD[to]}
            {reason ? <> — “{reason}”</> : null}
          </>
        ),
      };
    }

    case TaskEventType.MEMBER: {
      const { role, to } = event.data;
      return {
        icon: <TbUser size={13} />,
        tone: "neutral",
        line: to ? (
          <>
            <strong>{who}</strong> set the {role}
            {to.assigneeRole ? <> to the {to.assigneeRole} role</> : null}
            {to.assigneeId ? <> to {nameOf(String(to.assigneeId))}</> : null}
          </>
        ) : (
          <>
            <strong>{who}</strong> cleared the {role}
          </>
        ),
      };
    }

    case TaskEventType.DEADLINE:
      return {
        icon: <TbClock size={13} />,
        tone: "neutral",
        line: event.data.to ? (
          <>
            <strong>{who}</strong> set a {event.data.to.days}
            {event.data.to.businessDays ? " working" : " calendar"}-day deadline
          </>
        ) : (
          <>
            <strong>{who}</strong> removed the deadline
          </>
        ),
      };

    case TaskEventType.DEADLINE_PASSED:
      return {
        icon: <TbAlertTriangle size={13} />,
        tone: "warning",
        line: <>Deadline passed</>,
      };

    case TaskEventType.DETAILS:
      return {
        icon: <TbPencil size={12} />,
        tone: "muted",
        line: (
          <>
            <strong>{who}</strong> edited {event.data.fields.join(", ")}
          </>
        ),
      };

    case TaskEventType.SYNCED:
      return {
        icon: <TbRefreshDot size={13} />,
        tone: "muted",
        line: event.data.added ? (
          <>Added by a template update</>
        ) : (
          <>Template update changed {event.data.fields.join(", ")}</>
        ),
      };

    case TaskEventType.NOTIFIED: {
      const { trigger, recipients } = event.data;
      return {
        icon: <TbMail size={12} />,
        tone: "muted",
        line: (
          <>
            {trigger === "overdue"
              ? "Overdue reminder"
              : trigger === "unresolved"
                ? "Blocked alert"
                : "Assignment notice"}{" "}
            sent to {recipients.map((person) => nameOf(String(person.userId)) || person.email).join(", ")}
          </>
        ),
      };
    }

    default:
      return { icon: <TbPencil size={12} />, tone: "muted", line: <>Updated</> };
  }
};

interface TaskActivityProps {
  events: TaskEvent[];
  nameOf: (id?: string) => string;
}

export const TaskActivity = ({ events, nameOf }: TaskActivityProps) => {
  const [showReminders, setShowReminders] = useState(false);

  const visible = useMemo(() => {
    const filtered = showReminders ? events : events.filter((event) => event.type !== TaskEventType.NOTIFIED);
    return [...filtered].sort((a, b) => +new Date(b.at) - +new Date(a.at));
  }, [events, showReminders]);

  const reminderCount = events.filter((event) => event.type === TaskEventType.NOTIFIED).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Typography.Text
          style={{
            fontSize: 11,
            fontWeight: 650,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: taskUi.textFaint,
            flexGrow: 1,
          }}
        >
          Activity
        </Typography.Text>

        {reminderCount > 0 && (
          <span className="flex items-center gap-1.5">
            <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
              Reminders ({reminderCount})
            </Typography.Text>
            <Switch size="small" checked={showReminders} onChange={setShowReminders} />
          </span>
        )}
      </div>

      {visible.length === 0 ? (
        <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>Nothing recorded yet</Typography.Text>
      ) : (
        <div className="flex flex-col">
          {visible.map((event, i) => {
            const { icon, line, tone } = format(event, nameOf);
            const newDay = i === 0 || dayKey(event.at) !== dayKey(visible[i - 1].at);

            return (
              <div key={String(event._id)}>
                {newDay && (
                  <Typography.Text
                    style={{
                      fontSize: 11,
                      color: taskUi.textFaint,
                      display: "block",
                      marginTop: i === 0 ? 0 : 12,
                      marginBottom: 6,
                    }}
                  >
                    {new Date(event.at).toLocaleDateString(undefined, {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    })}
                  </Typography.Text>
                )}

                <div className="flex gap-2.5">
                  <span className="flex flex-col items-center" style={{ width: 22, flexShrink: 0 }}>
                    <span
                      className="flex items-center justify-center"
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "#fff",
                        border: `1px solid ${taskUi.border}`,
                        color: TONE_COLOR[tone],
                        flexShrink: 0,
                      }}
                    >
                      {icon}
                    </span>
                    {i < visible.length - 1 && (
                      <span style={{ width: 1, flexGrow: 1, minHeight: 10, background: taskUi.hairline }} />
                    )}
                  </span>

                  <span className="flex min-w-0 flex-grow flex-col" style={{ paddingBottom: 12 }}>
                    <Typography.Text style={{ fontSize: 12.5, color: taskUi.text }}>{line}</Typography.Text>
                    <Tooltip title={new Date(event.at).toLocaleString()}>
                      <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint }}>
                        {relative(event.at)}
                      </Typography.Text>
                    </Tooltip>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface TaskDrawerProps {
  task?: {
    _id: unknown;
    name: string;
    description?: string;
    status: TaskStatus;
    dueAt?: Date;
    skipReason?: string;
    events?: TaskEvent[];
  };
  open: boolean;
  onClose: () => void;
  nameOf: (id?: string) => string;
  /** Rendered above the timeline — members, outcome, whatever the board already knows. */
  facts?: React.ReactNode;
}

export const TaskDrawer = ({ task, open, onClose, nameOf, facts }: TaskDrawerProps) => (
  <Drawer open={open} onClose={onClose} width={460} title={task?.name} styles={{ body: { padding: 16 } }}>
    {!task ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No task selected" />
    ) : (
      <div className="flex flex-col gap-5 p-3">
        <div className="flex flex-col gap-2">
          {task.description && (
            <Typography.Text style={{ fontSize: 12.5, color: "#6b6b76", lineHeight: 1.45 }}>
              {task.description}
            </Typography.Text>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Tag bordered style={neutralTag}>
              {STATUS_WORD[task.status]}
            </Tag>
            {task.dueAt && (
              <Tag bordered style={neutralTag}>
                {describeDue(task.dueAt, false)}
              </Tag>
            )}
          </div>

          {task.skipReason && (
            <Typography.Text style={{ fontSize: 12, color: taskUi.textFaint }}>
              Skipped — “{task.skipReason}”
            </Typography.Text>
          )}
        </div>

        {facts}

        <TaskActivity events={task.events ?? []} nameOf={nameOf} />
      </div>
    )}
  </Drawer>
);
